import React, { useEffect, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDataProvider, useVersion } from 'react-admin';
import { Box, Typography, CircularProgress, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import { RegionTimeWindowDto } from '@vatos-pas/common';

import { SnackbarContainer } from 'components/standalone';
import { Show } from 'components/Show';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { AppRole } from 'providers/roles';
import { RegionTimeWindowList } from 'components/RegionTimeWindowTable/RegionTimeWindowList';
import { RegionTimeWindowTableDataRow } from 'components/RegionTimeWindowTable/types';
import { TABLE_COLUMNS } from 'components/RegionTimeWindowTable/constants';

import { RegionContainer } from '../containers/useRegionContainer';
import { RegionForm } from '../components/RegionForm';
import { MOBILE_TOOLBAR_TOTAL } from 'components/standalone/FormToolbar';

interface RouteParams {
  id: string;
}

const RegionShowInner: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const { id } = useParams<RouteParams>();
  const { notify } = SnackbarContainer.useContainer();
  const { hasRole } = PermissionsProvider.useContainer();
  const canEdit = hasRole(AppRole.SuperAdmin);

  const {
    selectedRegion,
    isLoading,
    regionLoaded,
    regionError,
    loadRegionById,
    supervisors,
    supervisorsLoading,
    loadSupervisors,
  } = RegionContainer.useContainer();

  const version = useVersion();

  const regionId = selectedRegion?.id;

  const [timeWindowsLoading, setTimeWindowsLoading] = useState(false);
  const [regionTimeWindows, setRegionTimeWindows] = useState<
    RegionTimeWindowDto[] | null
  >(null);

  const loadTimeWindows = useCallback(async () => {
    setTimeWindowsLoading(true);
    try {
      const result = await dataProvider.getList<RegionTimeWindowDto>(
        'region-time-window',
        {
          filter: { regionId: id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'windowStartTime', order: 'ASC' },
        },
      );

      if (result?.data) {
        setRegionTimeWindows(result.data);
      }
    } catch (err) {
      notify('Failed to load region time windows.', 'warning');
    }
    setTimeWindowsLoading(false);
  }, [dataProvider, id, notify]);

  useEffect(() => {
    if (!id) return;
    loadRegionById(id);
  }, [id, version, loadRegionById]);

  useEffect(() => {
    if (regionError || (regionLoaded && !selectedRegion)) {
      notify('Element does not exist', 'error');
      history.push('/region');
    }
  }, [regionError, regionLoaded, selectedRegion, notify, history]);

  useEffect(() => {
    if (!regionId) return;
    loadSupervisors(regionId);
    loadTimeWindows();
  }, [regionId, loadSupervisors, loadTimeWindows]);

  const handleEdit = useCallback(() => {
    history.push(`/region/${id}`);
  }, [history, id]);

  const handleGoBack = useCallback(() => {
    history.push('/region');
  }, [history]);

  const rows: RegionTimeWindowTableDataRow[] | null =
    regionTimeWindows?.map((rtw, index) => ({
      id: rtw.id,
      incrementalId: index + 1,
      windowStart: rtw.windowStartTime,
      windowEnd: rtw.windowEndTime,
    })) || null;

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedRegion) {
    return (
      <Box className={classes.createBox}>
        <Typography>Region not found</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.createBox}>
      <Box className={classes.topToolbar}>
        {canEdit && (
          <Button
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            size="small"
          >
            Edit
          </Button>
        )}
      </Box>

      <Box className={classes.formContainer}>
        <RegionForm
          mode="show"
          initialValues={selectedRegion}
          loading={false}
          onGoBack={handleGoBack}
          supervisors={supervisors}
          supervisorsLoading={supervisorsLoading}
        />
      </Box>

      <Show
        condition={!timeWindowsLoading}
        fallback={
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        }
      >
        <Box className={classes.timeWindowsSection}>
          <Typography
            className={classes.timeWindowsHeading}
            variant="h6"
            component="h3"
          >
            H/O Service Time Windows
          </Typography>

          <Box maxWidth="480px">
            <RegionTimeWindowList
              rows={rows}
              regionId={id}
              columns={TABLE_COLUMNS}
            />
          </Box>
        </Box>
      </Show>
    </Box>
  );
};

export const RegionsShow: React.FC = () => {
  return (
    <RegionContainer.Provider>
      <RegionShowInner />
    </RegionContainer.Provider>
  );
};

const useStyles = makeStyles(theme => ({
  createBox: {
    maxWidth: '1500px',
  },
  topToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    minHeight: 'auto',
  },
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(8),
  },
  timeWindowsSection: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: MOBILE_TOOLBAR_TOTAL + 20,
    },
  },
  timeWindowsHeading: {
    marginBottom: theme.spacing(2),
  },
}));

export default RegionsShow;
